import React from 'react'
import { Company } from '../../../types/intellijetnet'
import { AircraftOverride, TransactionOverride, TakePriceModifier } from '../../../types/database'
import InlineCompanyEditor from '../../inline-editors/InlineCompanyEditor'
import { CellBaseProps, resolveOverrideContext, isFieldEditing, overrideEditableClass } from './types'
import type { EditingCellFieldName } from '../types'

interface CompanyCellProps extends CellBaseProps {
  fieldType: 'owner' | 'broker'
  companyMap: Map<number, Company>
  handleCompanySave: (aircraftId: number, fieldType: 'owner' | 'broker', companyId: number | null, customValue: string | null) => Promise<void>
  handleTransactionCompanySave: (transactionId: string, fieldType: 'owner' | 'broker', companyId: number | null, customValue: string | null) => Promise<void>
}

/**
 * Renders the Owner or Broker column cell. Shared component for both since
 * the logic is identical — only the field name and display label differ.
 */
export function CompanyCell(props: CompanyCellProps) {
  const { aircraft, cellBorderClass, companyMap, fieldType, aircraftTransaction,
    handleStartEdit, handleStartTransactionEdit, handleCancelEdit,
    handleCompanySave, handleTransactionCompanySave } = props

  const { isSoldWithTransaction, isEditable, transOverride, override } = resolveOverrideContext(props)
  const fieldName: EditingCellFieldName = fieldType
  const customFieldName = `${fieldType}_custom` as 'owner_custom' | 'broker_custom'

  const hasOverride = isSoldWithTransaction
    ? (transOverride?.[fieldType] != null || transOverride?.[customFieldName] != null || aircraftTransaction![fieldType] != null || aircraftTransaction![customFieldName] != null)
    : (override?.[fieldType] != null || override?.[customFieldName] != null)

  const handleClick = isEditable ? (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isSoldWithTransaction) {
      handleStartTransactionEdit(aircraft.aircraft_id, aircraftTransaction!.transaction_id, fieldName)
    } else {
      handleStartEdit(aircraft.aircraft_id, fieldName)
    }
  } : undefined

  let displayValue = '-'
  if (isSoldWithTransaction) {
    if (transOverride?.[customFieldName]) displayValue = transOverride[customFieldName] as string
    else if (transOverride?.[fieldType]) displayValue = companyMap.get(transOverride[fieldType] as number)?.company_name || '-'
    else if (aircraftTransaction![customFieldName]) displayValue = aircraftTransaction![customFieldName] as string
    else if (aircraftTransaction![fieldType]) displayValue = companyMap.get(aircraftTransaction![fieldType] as number)?.company_name || '-'
  } else {
    if (override?.[customFieldName]) displayValue = override[customFieldName] as string
    else if (override?.[fieldType]) displayValue = companyMap.get(override[fieldType] as number)?.company_name || '-'
  }

  const currentCompanyId = isSoldWithTransaction
    ? (transOverride?.[fieldType] as number ?? aircraftTransaction![fieldType] as number ?? null)
    : (override?.[fieldType] as number ?? null)
  const currentCustomValue = isSoldWithTransaction
    ? (transOverride?.[customFieldName] as string ?? aircraftTransaction![customFieldName] as string ?? null)
    : (override?.[customFieldName] as string ?? null)

  const isEditing = isFieldEditing(props, fieldName, isSoldWithTransaction)

  return (
    <td className={`px-2 py-2 text-sm text-gray-900 text-left ${cellBorderClass} relative overflow-hidden`}>
      <>
        <div
          className={`break-words ${overrideEditableClass(hasOverride, isEditable)}`}
          onClick={handleClick}
        >
          {displayValue}
        </div>
        {isEditing && (
          <InlineCompanyEditor
            aircraftId={aircraft.aircraft_id}
            companyId={currentCompanyId}
            customValue={currentCustomValue}
            fieldName={fieldType === 'owner' ? 'Owner' : 'Broker'}
            onSave={async (companyId, customValue) => {
              if (isSoldWithTransaction) {
                await handleTransactionCompanySave(aircraftTransaction!.transaction_id, fieldType, companyId, customValue)
              } else {
                await handleCompanySave(aircraft.aircraft_id, fieldType, companyId, customValue)
              }
            }}
            onCancel={handleCancelEdit}
          />
        )}
      </>
    </td>
  )
}

